// PlaceholderPage.tsx

import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Stack,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import {
  ConstructionRounded,
  NotificationsActiveRounded,
  AutoAwesomeRounded,
  SpeedRounded,
  ShieldRounded,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import eventService from "@/services/eventService";
import axios from "axios";

export const PlaceholderPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">("success");

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || loading || subscribed) return;

    setLoading(true);
    try {
      await eventService.subscribe({
        email: email.trim(),
        topics: ["newsletter", "placeholder-launch"],
      });

      setSubscribed(true);
      setToastMessage(t("pages.placeholder.toastSuccess"));
      setToastSeverity("success");
      setToastOpen(true);
    } catch (error: unknown) {
      console.error("Event subscription failed:", error);
      let errorText = "Subscription failed. Please try again.";

      if (axios.isAxiosError(error)) {
        const backendMsg = error.response?.data?.message;
        if (typeof backendMsg === "string") {
          errorText = backendMsg;
        } else if (Array.isArray(backendMsg) && typeof backendMsg[0] === "string") {
          errorText = backendMsg[0];
        }
      }

      setToastMessage(errorText);
      setToastSeverity("error");
      setToastOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const featureHighlights = [
    {
      icon: <SpeedRounded color="primary" />,
      title: t("pages.placeholder.features.performance.title"),
      desc: t("pages.placeholder.features.performance.desc"),
    },
    {
      icon: <AutoAwesomeRounded color="primary" />,
      title: t("pages.placeholder.features.learningTools.title"),
      desc: t("pages.placeholder.features.learningTools.desc"),
    },
    {
      icon: <ShieldRounded color="primary" />,
      title: t("pages.placeholder.features.verifiedContent.title"),
      desc: t("pages.placeholder.features.verifiedContent.desc"),
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        py: 8,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Decorative Gradient Blobs */}
      <Box
        sx={{
          position: "absolute",
          top: "-10%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "600px",
          height: "600px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, isDark ? 0.15 : 0.08)} 0%, rgba(0,0,0,0) 70%)`,
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* Hero Card Container */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 1,
            bgcolor: "background.paper",
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: isDark ? "0 20px 40px rgba(0,0,0,0.4)" : "0 20px 40px rgba(0,0,0,0.04)",
            textAlign: "center",
          }}
        >
          {/* Animated Status Badge */}
          <Chip
            icon={<ConstructionRounded fontSize="small" />}
            label={t("pages.placeholder.badge")}
            color="warning"
            variant="outlined"
            sx={{
              px: 1,
              py: 2.2,
              fontWeight: 700,
              fontSize: "0.85rem",
              borderRadius: 1,
              mb: 3,
            }}
          />

          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.02em",
              fontSize: { xs: "2.25rem", sm: "3rem", md: "3.5rem" },
              mb: 2,
              color: "text.primary",
            }}
          >
            {t("pages.placeholder.title")}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: "text.secondary",
              fontWeight: 400,
              maxWidth: "600px",
              mx: "auto",
              mb: 5,
              lineHeight: 1.6,
            }}
          >
            {t("pages.placeholder.subtitle")}
          </Typography>

          {/* Email Subscription Box */}
          <Box
            component="form"
            onSubmit={handleSubscribe}
            sx={{
              maxWidth: "500px",
              mx: "auto",
              mb: 6,
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                fullWidth
                placeholder={t("pages.placeholder.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                disabled={subscribed || loading}
                required
                slotProps={{
                  input: {
                    sx: { borderRadius: 1 },
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={subscribed || loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <NotificationsActiveRounded />}
                sx={{
                  py: 1.5,
                  px: 3,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  borderRadius: 1,
                  textTransform: "none",
                }}
              >
                {subscribed ? t("pages.placeholder.notifiedBtn") : t("pages.placeholder.notifyBtn")}
              </Button>
            </Stack>
          </Box>

          {/* Feature Highlights Grid */}
          <Grid container spacing={3} sx={{ textAlign: "left", pt: 2 }}>
            {featureHighlights.map((feature, idx) => (
              <Grid size={{ xs: 12, md: 4 }} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: "100%",
                    borderRadius: 1,
                    bgcolor: isDark ? alpha(theme.palette.background.default, 0.5) : alpha(theme.palette.primary.main, 0.03),
                    border: `1px solid ${theme.palette.divider}`,
                    transition: "transform 0.2s, border-color 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <Box sx={{ mb: 1.5 }}>{feature.icon}</Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: "text.primary" }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
                    {feature.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>

      {/* Confirmation / Error Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 1 }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlaceholderPage;
